import { useCallback, useRef } from 'react';
import { importEndedEvent } from '../tracking/tracking';

type BatchTrackingState = {
  totalItems: number;
  completedCount: number;
  successCount: number;
  failureCount: number;
};

/**
 * Hook to track multiple batch uploads and fire importEndedEvent when each batch completes.
 * Tracks success/failure counts for each batch independently.
 */
export function useBatchTracking() {
  // Map of batchId -> tracking state for multiple concurrent batches
  const batchesRef = useRef<Map<string, BatchTrackingState>>(new Map());

  /**
   * Check if batch is complete and fire importEndedEvent
   */
  const checkBatchCompletion = useCallback(
    (batchId: string, batch: BatchTrackingState) => {
      if (batch.totalItems > 0 && batch.completedCount === batch.totalItems) {
        // Fire event with final counts
        importEndedEvent(batch.successCount, batch.failureCount);

        // Delete batch from map after logging event
        batchesRef.current.delete(batchId);
      }
    },
    [],
  );

  /**
   * Initialize tracking for a new batch
   */
  const initializeBatch = useCallback((batchId: string, totalItems: number) => {
    batchesRef.current.set(batchId, {
      totalItems,
      completedCount: 0,
      successCount: 0,
      failureCount: 0,
    });
  }, []);

  /**
   * Initialize tracking for multiple items, grouped by their batchId
   */
  const initializeBatchesFromItems = useCallback(
    <T extends { batchId?: string }>(items: T[]) => {
      const batchGroups = new Map<string, T[]>();
      items.forEach((item) => {
        if (item.batchId) {
          const groupItems = batchGroups.get(item.batchId) || [];
          groupItems.push(item);
          batchGroups.set(item.batchId, groupItems);
        }
      });
      batchGroups.forEach((groupItems, batchId) => {
        initializeBatch(batchId, groupItems.length);
      });
    },
    [initializeBatch],
  );

  /**
   * Mark an item as successfully completed
   */
  const markItemSuccess = useCallback(
    (batchId: string) => {
      const batch = batchesRef.current.get(batchId);
      if (!batch) return;

      // Use immutable update to prevent race conditions
      const updatedBatch: BatchTrackingState = {
        ...batch,
        successCount: batch.successCount + 1,
        completedCount: batch.completedCount + 1,
      };

      batchesRef.current.set(batchId, updatedBatch);
      checkBatchCompletion(batchId, updatedBatch);
    },
    [checkBatchCompletion],
  );

  /**
   * Mark an item as failed
   */
  const markItemFailure = useCallback(
    (batchId: string) => {
      const batch = batchesRef.current.get(batchId);
      if (!batch) return;

      // Use immutable update to prevent race conditions
      const updatedBatch: BatchTrackingState = {
        ...batch,
        failureCount: batch.failureCount + 1,
        completedCount: batch.completedCount + 1,
      };

      batchesRef.current.set(batchId, updatedBatch);
      checkBatchCompletion(batchId, updatedBatch);
    },
    [checkBatchCompletion],
  );

  return {
    initializeBatch,
    initializeBatchesFromItems,
    markItemSuccess,
    markItemFailure,
  };
}
